import {
  isNonAdjacentIdentifierOrder,
  type IdentifierIntakeContextOutput,
  type TEntry,
} from '@schemas'
import type { EntryIntakeState } from './entry-intake-state'

export type IntakeContextRequestBoundary = {
  exhibitorId: string
  generation: number
  revision: number
  codes: string[]
}

export function intakeContextCodes(state: EntryIntakeState, exhibitorId: string) {
  return [
    ...new Set([
      ...state.availableCodes,
      ...state.pendingEntries
        .filter(entry => entry.exhibitor.id === exhibitorId)
        .flatMap(entry => [
          entry.code,
          entry.syncAttempt?.request.entryCode ?? '',
          entry.syncAttempt?.createdEntry?.entryCode ?? '',
        ]),
      ...(state.localAssignments ?? [])
        .filter(entry => entry.exhibitorId === exhibitorId)
        .map(entry => entry.code),
    ]),
  ].filter(code => /^[1-9][0-9]{3}$/.test(code))
}

export function applyIntakeContext(
  state: EntryIntakeState,
  boundary: IntakeContextRequestBoundary,
  response: IdentifierIntakeContextOutput
): EntryIntakeState {
  if (state.assignmentContexts?.[boundary.exhibitorId]?.generation !== boundary.generation)
    return state
  const orders = { ...state.identifierOrders }
  for (const candidate of response.candidates) {
    if (candidate.orderIndex !== null && candidate.status !== 'unknown')
      orders[candidate.value] = candidate.orderIndex
  }
  return {
    ...state,
    identifierOrders: orders,
    assignmentContexts: {
      ...state.assignmentContexts,
      [boundary.exhibitorId]: {
        generation: boundary.generation,
        isShowStaff: response.isShowStaff,
        complete: response.complete,
        assignedOrderIndexes: response.assignedOrderIndexes,
        // Only codes this entrant cannot use are kept. A pool code with a known
        // allocation position is claimable unless the snapshot ruled it out.
        candidates: Object.fromEntries(
          response.candidates
            .filter(candidate => candidate.status !== 'claimable')
            .map(candidate => [candidate.value, candidate.status])
        ),
      },
    },
    // Only an authoritative snapshot begun after a creation can replace that
    // local assignment. Pending claims are always consulted independently.
    localAssignments: (state.localAssignments ?? []).filter(
      assignment =>
        !response.complete ||
        assignment.exhibitorId !== boundary.exhibitorId ||
        assignment.revision > boundary.revision
    ),
  }
}

export function compatibleIntakeCode(
  state: EntryIntakeState,
  exhibitorId: string
): string | undefined {
  const context = state.assignmentContexts?.[exhibitorId]
  if (!context?.complete) return undefined
  const assigned: number[] = []
  for (const entry of state.pendingEntries.filter(entry => entry.exhibitor.id === exhibitorId)) {
    const code =
      entry.syncAttempt?.createdEntry?.entryCode ??
      (entry.syncAttempt ? (entry.syncAttempt.request.entryCode ?? 'TMP') : entry.code)
    const index = state.identifierOrders?.[code]
    if (index === undefined || code.startsWith('TMP')) return undefined
    assigned.push(index)
  }
  for (const entry of (state.localAssignments ?? []).filter(
    entry => entry.exhibitorId === exhibitorId
  )) {
    const index = state.identifierOrders?.[entry.code] ?? entry.orderIndex
    if (index === null || index === undefined) return undefined
    assigned.push(index)
  }
  return state.availableCodes.find(code => {
    const index = state.identifierOrders?.[code]
    return (
      // IDs fetched after the snapshot arrive with their own allocation position.
      (context.candidates[code] ?? 'claimable') === 'claimable' &&
      index !== undefined &&
      // A claimable candidate may already be reserved for this entrant. The
      // server excludes that same value, but still enforces all local claims.
      isNonAdjacentIdentifierOrder(index, [
        ...context.assignedOrderIndexes.filter(order => order !== index),
        ...assigned,
      ])
    )
  })
}

export function recordIntakeAssignment(state: EntryIntakeState, entry: TEntry): EntryIntakeState {
  if (!entry.id || !entry.exhibitorId || !entry.entryCode) return state
  return {
    ...state,
    localAssignments: [
      ...(state.localAssignments ?? []).filter(record => record.entryId !== entry.id),
      {
        entryId: entry.id,
        exhibitorId: entry.exhibitorId,
        code: entry.entryCode,
        orderIndex: state.identifierOrders?.[entry.entryCode] ?? null,
        revision: (state.revision ?? 0) + 1,
      },
    ],
  }
}
